import React, { useEffect, useMemo, useState } from 'react'
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'

import styles, { colors } from '../styles/global'
import Icon from '../Icon'
import Button from '../UIControls/Button'
import ProjectSection, { ProjectSectionBody } from '../TaskListView/ProjectSection'
import ProjectHeader from '../TaskListView/Header/ProjectHeader'
import { translate, useTranslator } from '../../i18n/TranslationService'
import { setNavigationRoute } from '../../redux/actions'
import { DV_TAB_ROOT_WIKI } from '../../utils/TabNavigationConstants'
import { checkIfSelectedProject } from '../SettingsView/ProjectsSettings/ProjectHelper'
import URLsWiki, {
    URL_PROJECT_WIKI,
    URL_PROJECT_WIKI_PAGE,
    URL_PROJECT_WIKI_PAGE_CHANGE,
    URL_PROJECT_WIKI_PAGE_CHANGES_SINCE,
} from '../../URLSystem/Wiki/URLsWiki'
import WikiMarkdown, { getWikiSourceLabel, isOpenableWikiSource } from './WikiMarkdown'
import WikiPageHistory from './WikiPageHistory'
import WikiPageChange from './WikiPageChange'
import WikiPageRecentChanges from './WikiPageRecentChanges'
import WikiFeedbackButton from './WikiFeedbackButton'
import { formatWikiDate, isKnowledgeBaseRunning, useKnowledgeBaseDoc, useKnowledgePages } from './useKnowledgeBase'
import {
    setWikiSelectedRange,
    setWikiSelectedSlug,
    useWikiSelectedRevision,
    useWikiSelectedSince,
    useWikiSelectedSlug,
} from './wikiNavigationState'
import { getDefaultWikiRangeBase } from './wikiRecentChanges'
import { openProjectWikiSettings, openWikiSource } from './wikiLinks'
import { getWikiIndexPages } from './wikiIndexOrder'
import {
    getKnowledgeBaseLastSuccessfulRunAt,
    isKnowledgeBasePausedAtMonthlyCap,
    parseKnowledgeSourceKey,
    splitKnowledgePageSources,
} from '../../functions/KnowledgeBase/knowledgeBaseModel'

function WikiStatusLine({ knowledgeBase, pendingRebuilds }) {
    const lines = []
    if (isKnowledgeBaseRunning(knowledgeBase)) lines.push(translate('The wiki is being updated right now'))
    else if (getKnowledgeBaseLastSuccessfulRunAt(knowledgeBase || {})) {
        // The last run that caught up, not merely the last attempt: a failed run updated nothing.
        lines.push(
            translate('Wiki last updated', {
                date: formatWikiDate(getKnowledgeBaseLastSuccessfulRunAt(knowledgeBase)),
            })
        )
    }
    if (knowledgeBase && knowledgeBase.enabled !== true) lines.push(translate('Wiki paused hint'))
    if (isKnowledgeBasePausedAtMonthlyCap(knowledgeBase || {})) {
        lines.push(translate('Wiki paused at monthly cap', { date: formatWikiDate(knowledgeBase.nextRunAt) }))
    }
    if (pendingRebuilds > 0) lines.push(translate('Wiki pages being rebuilt', { amount: pendingRebuilds }))
    if (!lines.length) return null
    return (
        <View testID="wiki-status" style={localStyles.statusLine}>
            {lines.map(line => (
                <Text key={line} style={localStyles.statusText}>
                    {line}
                </Text>
            ))}
        </View>
    )
}

function WikiEmptyState({ projectId, knowledgeBase }) {
    const building = knowledgeBase?.enabled === true
    return (
        <View testID={building ? 'wiki-building' : 'wiki-not-set-up'} style={localStyles.emptyState}>
            <Icon name="book-open" size={32} color={colors.Text03} />
            <Text style={localStyles.emptyTitle}>
                {translate(building ? 'The wiki is being built' : 'This project has no wiki yet')}
            </Text>
            <Text style={localStyles.emptyText}>
                {translate(building ? 'Wiki first build hint' : 'Wiki explanation')}
            </Text>
            {!building && (
                <Button
                    title={translate('Set up wiki')}
                    icon="settings"
                    onPress={() => openProjectWikiSettings(projectId)}
                    buttonStyle={localStyles.emptyButton}
                />
            )}
        </View>
    )
}

// Sits at the very top of the index. Narrows the list by title and summary, the two things a row shows.
function WikiSearch({ value, onChange }) {
    return (
        <View testID="wiki-search" style={localStyles.searchBox}>
            <Icon name="search" size={18} color={colors.Text03} />
            <TextInput
                testID="wiki-search-input"
                value={value}
                onChangeText={onChange}
                placeholder={translate('Search wiki pages')}
                placeholderTextColor={colors.Text03}
                accessibilityLabel={translate('Search wiki pages')}
                autoCapitalize="none"
                autoCorrect={false}
                numberOfLines={1}
                style={localStyles.searchInput}
            />
            {!!value && (
                <TouchableOpacity
                    testID="wiki-search-clear"
                    onPress={() => onChange('')}
                    accessibilityLabel={translate('Clear wiki search')}
                    style={localStyles.searchClear}
                >
                    <Icon name="x" size={18} color={colors.Text03} />
                </TouchableOpacity>
            )}
        </View>
    )
}

function WikiIndex({ pages, selectedSlug, onSelect }) {
    if (pages.length === 0) {
        return (
            <Text testID="wiki-search-empty" style={localStyles.searchEmpty}>
                {translate('No wiki pages match your search')}
            </Text>
        )
    }
    return (
        <View testID="wiki-index">
            {pages.map(page => {
                const selected = page.slug === selectedSlug
                return (
                    <TouchableOpacity
                        key={page.slug}
                        testID={`wiki-index-${page.slug}`}
                        style={[localStyles.indexItem, selected && localStyles.indexItemSelected]}
                        onPress={() => onSelect(page.slug)}
                        accessible={false}
                    >
                        <Text style={[localStyles.indexTitle, selected && localStyles.indexTitleSelected]}>
                            {page.title}
                        </Text>
                        {!!page.summary && (
                            <Text style={localStyles.indexSummary} numberOfLines={2}>
                                {page.summary}
                            </Text>
                        )}
                        {!!page.updatedAt && (
                            <Text testID={`wiki-index-updated-${page.slug}`} style={localStyles.indexUpdated}>
                                {translate('Wiki page last updated', { date: formatWikiDate(page.updatedAt) })}
                            </Text>
                        )}
                    </TouchableOpacity>
                )
            })}
        </View>
    )
}

function toSourceChips(keys) {
    return keys
        .map(key => {
            const parsed = parseKnowledgeSourceKey(key)
            return parsed ? { key, objectType: parsed.type, id: parsed.id, label: '' } : null
        })
        .filter(Boolean)
}

function WikiSourceChips({ projectId, sources, sourceLabels }) {
    return (
        <View style={localStyles.sourceList}>
            {sources.map(source => (
                <TouchableOpacity
                    key={source.key}
                    testID={`wiki-page-source-${source.key}`}
                    style={localStyles.sourceItem}
                    disabled={!isOpenableWikiSource(source.objectType)}
                    onPress={() => openWikiSource(projectId, source)}
                    accessible={false}
                >
                    <Text style={localStyles.sourceItemText} numberOfLines={1}>
                        {getWikiSourceLabel(source, sourceLabels)}
                    </Text>
                </TouchableOpacity>
            ))}
        </View>
    )
}

// The sources the text cites, and folded away below them the ones the page was also compiled from
// but no longer cites. Those stay on the page for the privacy withdrawal
// (knowledgeSourceInvalidation.js), so they are kept visible on request, never presented as if they
// backed the text.
function WikiPageSources({ projectId, page }) {
    const [showOthers, setShowOthers] = useState(false)
    const { cited, others } = useMemo(() => {
        const { citedSourceKeys, otherSourceKeys } = splitKnowledgePageSources(page)
        return { cited: toSourceChips(citedSourceKeys), others: toSourceChips(otherSourceKeys) }
    }, [page.sourceKeys, page.body])

    useEffect(() => {
        setShowOthers(false)
    }, [page.slug])

    if (!cited.length && !others.length) return null
    return (
        <View testID="wiki-page-sources" style={localStyles.sources}>
            <Text style={localStyles.sectionLabel}>{translate('Sources')}</Text>
            {cited.length > 0 && (
                <WikiSourceChips projectId={projectId} sources={cited} sourceLabels={page.sourceLabels} />
            )}
            {others.length > 0 && (
                <TouchableOpacity
                    testID="wiki-page-other-sources-toggle"
                    onPress={() => setShowOthers(!showOthers)}
                    accessible={false}
                >
                    <Text style={localStyles.historyToggle}>
                        {translate(showOthers ? 'Hide other wiki sources' : 'Show other wiki sources', {
                            amount: others.length,
                        })}
                    </Text>
                </TouchableOpacity>
            )}
            {showOthers && others.length > 0 && (
                <View testID="wiki-page-other-sources" style={localStyles.otherSources}>
                    <Text style={localStyles.otherSourcesHint}>{translate('Other wiki sources hint')}</Text>
                    <WikiSourceChips projectId={projectId} sources={others} sourceLabels={page.sourceLabels} />
                </View>
            )}
        </View>
    )
}

function WikiPage({ projectId, page, pageSlugs, onSelectPage, onBack }) {
    const [showHistory, setShowHistory] = useState(false)

    useEffect(() => {
        setShowHistory(false)
    }, [page.slug])

    return (
        <View testID={`wiki-page-${page.slug}`}>
            {onBack && (
                <TouchableOpacity style={localStyles.backLink} onPress={onBack} accessible={false}>
                    <Icon name="arrow-left" size={16} color={colors.Primary100} />
                    <Text style={localStyles.backLinkText}>{translate('All wiki pages')}</Text>
                </TouchableOpacity>
            )}
            <Text style={localStyles.pageTitle}>{page.title}</Text>
            <View style={localStyles.pageMeta}>
                <Text testID="wiki-page-updated" style={localStyles.pageMetaText}>
                    {page.updatedAt
                        ? translate('Wiki page last updated', { date: formatWikiDate(page.updatedAt) })
                        : ''}
                </Text>
                <View style={localStyles.pageActions}>
                    {page.revision > 0 && (
                        <Button
                            type={'ghost'}
                            icon={'git-commit'}
                            iconSize={16}
                            iconGap={8}
                            title={translate('Show changes')}
                            accessibilityLabel={translate('Show recent changes of this page')}
                            onPress={() =>
                                setWikiSelectedRange(projectId, page.slug, getDefaultWikiRangeBase(page.revision))
                            }
                            buttonStyle={localStyles.compactButton}
                        />
                    )}
                    <WikiFeedbackButton projectId={projectId} page={page} />
                </View>
            </View>
            {!!page.summary && <Text style={localStyles.pageSummary}>{page.summary}</Text>}

            <WikiMarkdown
                body={page.body}
                sourceLabels={page.sourceLabels}
                pageExists={slug => pageSlugs.has(slug)}
                onPressPage={onSelectPage}
                onPressSource={source => openWikiSource(projectId, source)}
            />

            <WikiPageSources projectId={projectId} page={page} />

            <View style={localStyles.pageFooter}>
                <Text style={localStyles.footerText}>
                    {translate('Wiki page revision', {
                        revision: page.revision,
                        date: formatWikiDate(page.updatedAt),
                    })}
                </Text>
                <TouchableOpacity onPress={() => setShowHistory(!showHistory)} accessible={false}>
                    <Text style={localStyles.historyToggle}>
                        {translate(showHistory ? 'Hide history' : 'Show history')}
                    </Text>
                </TouchableOpacity>
            </View>
            {showHistory && (
                <WikiPageHistory
                    projectId={projectId}
                    slug={page.slug}
                    currentRevision={page.revision}
                    onOpenChange={revision => setWikiSelectedSlug(projectId, page.slug, revision)}
                />
            )}
        </View>
    )
}

export function ProjectWiki({ project }) {
    const knowledgeBase = useKnowledgeBaseDoc(project.id)
    const { pages, pendingRebuilds, loaded } = useKnowledgePages(project.id)
    const [selectedSlug, setSelectedSlug] = useWikiSelectedSlug(project.id)
    const selectedRevision = useWikiSelectedRevision(project.id)
    const selectedSince = useWikiSelectedSince(project.id)
    // Kept here rather than in the index, so going into a page and back keeps the search.
    const [searchText, setSearchText] = useState('')

    const pageSlugs = useMemo(() => new Set(pages.map(page => page.slug)), [pages])
    const selectedPage = pages.find(page => page.slug === selectedSlug) || null
    // Newest first, by the same `updatedAt` each row shows as "Last updated".
    const indexPages = useMemo(() => getWikiIndexPages(pages, searchText), [pages, searchText])
    // The index and a page are one column on every screen: the index first, a page in its place.
    const shownPage = selectedPage
    // A change is shown for any page, archived or hidden ones included: the Updates feed links to
    // every change, and the page view only knows the pages people may read right now.
    const shownChange = selectedSlug && selectedRevision ? { slug: selectedSlug, revision: selectedRevision } : null
    // Everything a page changed since one of its revisions (AT-2878), opened from the page itself.
    const shownRange =
        !shownChange && selectedSlug && Number.isInteger(selectedSince)
            ? { slug: selectedSlug, since: selectedSince }
            : null

    useEffect(() => {
        if (shownChange) {
            URLsWiki.push(
                URL_PROJECT_WIKI_PAGE_CHANGE,
                null,
                project.id,
                shownChange.slug,
                selectedPage?.title,
                shownChange.revision
            )
        } else if (shownRange) {
            URLsWiki.push(
                URL_PROJECT_WIKI_PAGE_CHANGES_SINCE,
                null,
                project.id,
                shownRange.slug,
                selectedPage?.title,
                shownRange.since
            )
        } else if (shownPage) {
            URLsWiki.push(URL_PROJECT_WIKI_PAGE, null, project.id, shownPage.slug, shownPage.title)
        } else {
            URLsWiki.push(URL_PROJECT_WIKI, null, project.id)
        }
    }, [project.id, shownPage?.slug, shownChange?.slug, shownChange?.revision, shownRange?.slug, shownRange?.since])

    const loading = knowledgeBase === undefined || !loaded
    const changeView = shownChange && (
        <WikiPageChange
            key={`${shownChange.slug}-${shownChange.revision}`}
            projectId={project.id}
            slug={shownChange.slug}
            revision={shownChange.revision}
            pageSlugs={pageSlugs}
            onSelectRevision={revision => setWikiSelectedSlug(project.id, shownChange.slug, revision)}
            onOpenPage={setSelectedSlug}
            onBack={() => setSelectedSlug(null)}
        />
    )
    const rangeView = shownRange && (
        <WikiPageRecentChanges
            key={`${shownRange.slug}-since-${shownRange.since}`}
            projectId={project.id}
            slug={shownRange.slug}
            since={shownRange.since}
            pageSlugs={pageSlugs}
            onSelectSince={since => setWikiSelectedRange(project.id, shownRange.slug, since)}
            onOpenChange={revision => setWikiSelectedSlug(project.id, shownRange.slug, revision)}
            onOpenPage={setSelectedSlug}
            onBack={() => setSelectedSlug(null)}
        />
    )

    return (
        <ProjectSection projectId={project.id} projectColor={project.color} selected style={{ marginBottom: 32 }}>
            <ProjectHeader
                projectIndex={project.index}
                projectId={project.id}
                customRight={
                    <Button
                        type={'ghost'}
                        icon={'settings'}
                        noBorder
                        onPress={() => openProjectWikiSettings(project.id)}
                        accessibilityLabel={translate('Wiki settings')}
                        buttonStyle={localStyles.settingsButton}
                    />
                }
            />
            <ProjectSectionBody>
                {loading ? (
                    <Text style={localStyles.statusText}>{translate('Loading wiki')}</Text>
                ) : changeView ? (
                    changeView
                ) : rangeView ? (
                    rangeView
                ) : pages.length === 0 ? (
                    <WikiEmptyState projectId={project.id} knowledgeBase={knowledgeBase} />
                ) : (
                    <View style={localStyles.column}>
                        {!shownPage && <WikiSearch value={searchText} onChange={setSearchText} />}
                        <WikiStatusLine knowledgeBase={knowledgeBase} pendingRebuilds={pendingRebuilds} />
                        {shownPage ? (
                            <WikiPage
                                projectId={project.id}
                                page={shownPage}
                                pageSlugs={pageSlugs}
                                onSelectPage={setSelectedSlug}
                                onBack={() => setSelectedSlug(null)}
                            />
                        ) : (
                            <WikiIndex pages={indexPages} selectedSlug={null} onSelect={setSelectedSlug} />
                        )}
                    </View>
                )}
            </ProjectSectionBody>
        </ProjectSection>
    )
}

export default function WikiView() {
    useTranslator()
    const dispatch = useDispatch()
    const selectedProjectIndex = useSelector(state => state.selectedProjectIndex)
    const project = useSelector(state => state.loggedUserProjects[selectedProjectIndex])
    const mobile = useSelector(state => state.smallScreenNavigation)
    const isMiddleScreen = useSelector(state => state.isMiddleScreen)

    useEffect(() => {
        dispatch(setNavigationRoute(DV_TAB_ROOT_WIKI))
    }, [])

    return (
        <View
            testID="wiki-view"
            style={[
                localStyles.container,
                mobile ? localStyles.containerMobile : isMiddleScreen && localStyles.containerTablet,
            ]}
        >
            {checkIfSelectedProject(selectedProjectIndex) && project ? (
                <ProjectWiki key={project.id} project={project} />
            ) : (
                <View testID="wiki-choose-project" style={localStyles.emptyState}>
                    <Icon name="book-open" size={32} color={colors.Text03} />
                    <Text style={localStyles.emptyText}>{translate('Choose a project to open its wiki')}</Text>
                </View>
            )}
        </View>
    )
}

const localStyles = StyleSheet.create({
    container: {
        marginHorizontal: 104,
    },
    containerMobile: {
        marginHorizontal: 16,
    },
    containerTablet: {
        marginHorizontal: 56,
    },
    // An icon in the 56px project line, centred like the other header actions.
    settingsButton: {
        width: 32,
        height: 32,
        minHeight: 32,
        maxHeight: 32,
        paddingLeft: 0,
        paddingRight: 0,
        paddingVertical: 0,
        marginLeft: 8,
        alignSelf: 'center',
    },
    statusLine: {
        marginTop: 12,
        marginBottom: 4,
    },
    statusText: {
        ...styles.caption2,
        color: colors.Text03,
        marginTop: 8,
    },
    emptyState: {
        alignItems: 'center',
        paddingVertical: 40,
        paddingHorizontal: 16,
    },
    emptyTitle: {
        ...styles.title7,
        color: colors.Text01,
        marginTop: 12,
        textAlign: 'center',
    },
    emptyText: {
        ...styles.body2,
        color: colors.Text02,
        marginTop: 8,
        maxWidth: 480,
        textAlign: 'center',
    },
    emptyButton: {
        marginTop: 16,
    },
    // One readable column; on a wide screen a full-width line of prose is hard to follow.
    column: {
        maxWidth: 880,
    },
    searchBox: {
        flexDirection: 'row',
        alignItems: 'center',
        height: 40,
        marginTop: 12,
        paddingLeft: 12,
        paddingRight: 4,
        borderWidth: 1,
        borderRadius: 6,
        borderColor: colors.Grey400,
        backgroundColor: '#FFFFFF',
    },
    searchInput: {
        ...styles.body2,
        flex: 1,
        minWidth: 0,
        height: 38,
        color: colors.Text01,
        marginLeft: 8,
        paddingVertical: 0,
    },
    searchClear: {
        width: 32,
        height: 32,
        alignItems: 'center',
        justifyContent: 'center',
    },
    searchEmpty: {
        ...styles.body2,
        color: colors.Text02,
        paddingVertical: 16,
        paddingHorizontal: 12,
    },
    indexItem: {
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 4,
        marginBottom: 2,
    },
    indexItemSelected: {
        backgroundColor: colors.Grey200,
    },
    indexTitle: {
        ...styles.subtitle2,
        color: colors.Text01,
    },
    indexTitleSelected: {
        color: colors.Primary100,
    },
    indexSummary: {
        ...styles.caption2,
        color: colors.Text03,
        marginTop: 2,
    },
    indexUpdated: {
        ...styles.caption2,
        color: colors.Text03,
        marginTop: 2,
    },
    backLink: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 12,
        marginBottom: 8,
    },
    backLinkText: {
        ...styles.subtitle2,
        color: colors.Primary100,
        marginLeft: 4,
    },
    pageTitle: {
        ...styles.title5,
        color: colors.Text01,
        marginTop: 8,
    },
    pageMeta: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        marginTop: 4,
    },
    pageActions: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    // The same compact ghost button as "Give feedback" next to it (WikiFeedbackButton).
    compactButton: {
        height: 32,
        minHeight: 32,
        maxHeight: 32,
        paddingVertical: 4,
        paddingLeft: 8,
        paddingRight: 12,
        marginVertical: 4,
        marginRight: 4,
    },
    pageMetaText: {
        ...styles.caption2,
        color: colors.Text03,
        marginRight: 12,
    },
    pageSummary: {
        ...styles.body2,
        color: colors.Text02,
        marginTop: 4,
        marginBottom: 8,
    },
    sources: {
        marginTop: 16,
        borderTopWidth: 1,
        borderTopColor: colors.Grey300,
        paddingTop: 12,
    },
    sectionLabel: {
        ...styles.overline,
        color: colors.Text03,
        marginBottom: 8,
    },
    sourceList: {
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    sourceItem: {
        backgroundColor: colors.Grey200,
        borderRadius: 12,
        paddingHorizontal: 8,
        paddingVertical: 2,
        marginRight: 6,
        marginBottom: 6,
        maxWidth: 260,
    },
    sourceItemText: {
        ...styles.body2,
        color: colors.Text02,
    },
    otherSources: {
        marginTop: 8,
    },
    otherSourcesHint: {
        ...styles.caption2,
        color: colors.Text03,
        marginBottom: 6,
    },
    pageFooter: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        marginTop: 16,
    },
    footerText: {
        ...styles.caption2,
        color: colors.Text03,
    },
    historyToggle: {
        ...styles.caption2,
        color: colors.Primary100,
    },
})
